import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardContent,
  Chip,
  Collapse,
  Divider,
  Stack,
  Tooltip,
  IconButton,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import SyncIcon from '@mui/icons-material/Sync';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import PendingOutlinedIcon from '@mui/icons-material/PendingOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';

import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import { ProfileAvatar } from '../../components/DynamicForm';
import { formatTimeToMinute, prefixPeriod } from '../../components/DailyTimeline';

import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { useAccount } from '../../context/AccountContext';
import { humanReadableDate, useURLParams } from '../../common/AppUtils';
import { USER_ROLES } from '../../common/AppConstants';

const TITLE = 'Substitutes';
const API_URL = 'time-table/staff-substitute/';
const DOWNLOAD_API_URL = 'time-table/staff-substitute-download/';
const API_DATE_FORMAT = 'YYYY-MM-DD';

const NO_DATA_AVAILABLE_STRING =
  'No substitutes for this date. Mark staff attendance to have them allotted.';

// Same split-button + dropdown pattern as Schedule.js's timetable download
const DOWNLOAD_OPTIONS = [
  {
    label: 'Absentees',
    help_text: 'Absent teacher view',
    format: 'absentee', // default
    file_suffix: 'by_absentee',
  },
  {
    label: 'Substitutes',
    help_text: 'Substitute teacher view',
    format: 'substitute',
    file_suffix: 'by_substitute',
  },
];

const substituteColumns = [
  {
    attr: 'subject_session',
    editable: false,
    extra: false,
    label: 'Period',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Class needing cover',
  },
  {
    attr: 'absent_staff',
    editable: false,
    extra: false,
    label: 'Absent',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Staff being covered for',
  },
  {
    attr: 'substitute',
    editable: true,
    extra: false,
    label: 'Substitute',
    related: true,
    required: true,
    type: 'choice',
    help_text: 'Assigned substitute teacher',
  },
  {
    attr: 'edit',
    editable: true,
    extra: true,
    label: 'Edit',
    related: false,
    required: true,
    type: 'text',
  },
];

export default function StaffSubstitute() {
  const { addNotification } = useNotification();
  const { account } = useAccount();
  const { urlParam } = useURLParams();

  // Admin Viewer can see substitutes, only an Admin can reassign one
  const isAdmin =
    account?.user?.current_role?.toLowerCase() === USER_ROLES.ADMIN;

  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [downloadSelectedIndex, setDownloadSelectedIndex] = useState(0); // default first option
  const downloadOpen = Boolean(downloadAnchorEl);
  // Linked to from the attendance tile, which carries the date it was showing
  const [selectedDate, setSelectedDate] = useState(() => {
    const parsed = dayjs(urlParam.get("dt"), API_DATE_FORMAT);
    return parsed.isValid() ? parsed : dayjs();
  });
  const [rows, setRows] = useState([]);
  const [relatedData, setRelatedData] = useState({});
  const [marked, setMarked] = useState(false);
  const [substituteRun, setSubstituteRun] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEditRow, setSelectedEditRow] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const substituteMap = relatedData.substitute || {};
  const sessionMap = relatedData.subject_session || {};

  // Filtered data based on search, spans substitute, absentee and class
  const filteredRows = rows.filter(row_item =>
    [
      substituteMap[row_item.substitute]?.label,
      substituteMap[row_item.absent_staff]?.label,
      sessionMap[row_item.subject_session]?.label,
    ].some(value => value?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Covered/uncovered are just a count of rows[].substitute being set or null,
  // no separate backend call needed - refer StaffSubstitute.substitute
  const coveredCount = rows.filter(row_item => row_item.substitute).length;
  const uncoveredCount = rows.filter(row_item => !row_item.substitute).length;


  const refreshData = async (targetDate) => {
    const dateParam = (targetDate || selectedDate).format(API_DATE_FORMAT);
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      data: { date: dateParam },
      onSuccess: (data) => {
        setRows(data.rows || []);
        setRelatedData(data.relatedData || {});
        setMarked(data.marked);
        setSubstituteRun(data.substitute_run || null);
      },
      onFailure: (data) => {
        setRows([]);
        setRelatedData({});
        setMarked(false);
        setSubstituteRun(null);
      },
      addNotification,
    });

    setIsLoading(false);
  };

  const handleDownload = async (option) => {
    setIsDownloading(true);
    const dateParam = selectedDate.format(API_DATE_FORMAT);

    await httpRequest({
      method: 'GET',
      url: DOWNLOAD_API_URL,
      data: { date: dateParam, pdf_format: option.format },
      config: { responseType: 'blob' },
      onSuccess: (data) => {
        const pdfBlob = new Blob([data], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(pdfBlob);
        const downloadLink = document.createElement('a');
        downloadLink.href = blobUrl;
        downloadLink.download = `substitutes_${dateParam}_${option.file_suffix}.pdf`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.remove();
      },
      addNotification,
    });

    setIsDownloading(false);
  };

  useEffect(() => {
    refreshData(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    if (selectedEditRow !== null && selectedEditRow !== undefined) {
      // Only open the modal after the row has been selected
      setIsEditModalOpen(true);
    }
  }, [selectedEditRow]);

  const handleDateChange = (newValue) => {
    if (!newValue || !newValue.isValid()) {
      return;
    }
    setSelectedDate(newValue);
  };

  const handleEditClick = (row) => {
    setSelectedEditRow(row);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedEditRow(null);
  };

  const handleEditSave = async (updatedEditRow, onEditFailure) => {
    await httpRequest({
      method: 'PUT',
      url: API_URL,
      data: updatedEditRow,
      onSuccess: (data) => {
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === selectedEditRow.id ? { ...row, ...data } : row,
          ),
        );
        handleEditModalClose();
      },
      onFailure: (data) => {
        onEditFailure(data);
      },
      addNotification,
    });
  };

  const handleDelete = async (deletedRow) => {
    await httpRequest({
      method: 'DELETE',
      url: API_URL,
      data: deletedRow,
      onSuccess: () => {
        setRows((prevRows) => prevRows.filter((row) => row.id !== deletedRow.id));
        handleEditModalClose();
      },
      addNotification,
    });
  };

  const SubstituteCard = ({ substitute_dict }) => {
    const [expanded, setExpanded] = useState(false);
    const substitute_detail = substituteMap[substitute_dict.substitute];
    const absent_detail = substituteMap[substitute_dict.absent_staff];
    const session_detail = sessionMap[substitute_dict.subject_session] || {};
    const is_uncovered = !substitute_dict.substitute;

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          transition: 'box-shadow 0.3s',
          '&:hover': { boxShadow: 3 },
          ...(is_uncovered && {
            borderColor: 'error.light',
            bgcolor: 'rgba(211, 47, 47, 0.04)',
          }),
        }}
      >
        {/* Header matches Teachers.js's card - profile, name, edit */}
        <CardHeader
          avatar={
            is_uncovered ? (
              <ReportProblemOutlinedIcon color="error" />
            ) : (
              <ProfileAvatar dataDict={substitute_detail} />
            )
          }
          title={
            <Typography variant="h5" color={is_uncovered ? 'error.main' : 'text.primary'}>
              {is_uncovered ? 'No Substitute Found' : substitute_detail?.label}
            </Typography>
          }
          action={
            isAdmin && (
              <IconButton onClick={() => handleEditClick(substitute_dict)} size="small">
                <EditTwoToneIcon fontSize="small" />
              </IconButton>
            )
          }
          sx={{
            px: 2,
            py: 0,
            alignItems: 'center',
            '& .MuiCardHeader-action': { alignSelf: 'center' },
          }}
          slotProps={{ content: { sx: { py: 1.5 } } }}
        />
        <Divider />

        {/* Same row layout/fonts as FitnessPayments.js's PaymentCard - label left, value right */}
        <CardContent sx={{ pt: 1.5, pb: 1 }}>
          <Stack spacing={0.75}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Period</Typography>
              <Typography variant="body2">{prefixPeriod(session_detail.time_label)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Classroom</Typography>
              <Typography variant="body2">{session_detail.classroom || '-'}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Section</Typography>
              <Typography variant="body2">{session_detail.section}</Typography>
            </Box>
          </Stack>
        </CardContent>

        {/* Collapsible Content - same pattern as FitnessPayments.js's PaymentCard */}
        <Collapse in={expanded}>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <CardContent sx={{ bgcolor: '#FBFBFB', pt: 1.5, pb: 1 }}>
            <Stack spacing={0.75}>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Time</Typography>
                <Typography variant="body2">
                  {formatTimeToMinute(session_detail.start_time)} - {formatTimeToMinute(session_detail.end_time)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Subject</Typography>
                <Typography variant="body2">{session_detail.subject}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">Absentee</Typography>
                <Typography variant="body2">{absent_detail?.label}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Collapse>

        {/* Expand toggle - full width bottom */}
        <Box
          onClick={handleExpandClick}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 0.25,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <KeyboardDoubleArrowDownIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </Box>
      </Card>
    );
  };

  const SummaryTile = () => {
    // Text only, no chip - the run's outcome IS the headline on this page
    const [tileIcon, tileText] =
      !marked
        ? [<PendingOutlinedIcon color="warning" sx={{ fontSize: 24 }} />, 'Attendance Pending']
        : substituteRun?.status === 'completed'
          ? [<TaskAltIcon color="success" sx={{ fontSize: 24 }} />, 'Substitutes Assigned']
          : substituteRun?.status === 'error'
            ? [<ReportProblemOutlinedIcon color="warning" sx={{ fontSize: 24 }} />, 'Substitution Failed']
            : [<PendingOutlinedIcon color="warning" sx={{ fontSize: 24 }} />, 'Assigning Substitutes'];

    // Green only once the run has actually finished, not merely started
    const isSettled = marked && substituteRun?.status === 'completed';

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: isSettled ? 'success.light' : 'warning.light',
          bgcolor: isSettled
            ? 'rgba(40, 180, 99, 0.06)'
            : 'rgba(202, 111, 30, 0.06)',
        }}
      >
        <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            {tileIcon}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, minWidth: 0 }}>
              {/* Row 1: date, then run status */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  {humanReadableDate(selectedDate.format(API_DATE_FORMAT), true)}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  •
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {tileText}
                </Typography>
              </Box>

              {/* Row 2: coverage counts */}
              {rows.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={`${coveredCount} Covered`}
                    variant="outlined"
                    color="success"
                    sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                  <Chip
                    size="small"
                    label={`${uncoveredCount} Uncovered`}
                    variant="outlined"
                    color="error"
                    sx={{ height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and controls (fixed) */}
      <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            mb: 2,
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>

          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                format="DD MMM YYYY"
                slotProps={{
                  textField: { size: 'small', sx: { width: 170 } },
                }}
              />
            </LocalizationProvider>

            <Tooltip title="Refresh" arrow>
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => refreshData(selectedDate)}
                  disabled={isLoading}
                  sx={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 1 }}
                >
                  <SyncIcon
                    fontSize="small"
                    sx={{
                      animation: isLoading ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(-360deg)' },
                      },
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>

            <ButtonGroup variant="contained" color="primary">
              <Button
                startIcon={<CloudDownloadIcon />}
                onClick={() => handleDownload(DOWNLOAD_OPTIONS[downloadSelectedIndex])}
                disabled={isDownloading || rows.length === 0}
                sx={{ px: 3 }}
              >
                {isDownloading ? 'Downloading...' : DOWNLOAD_OPTIONS[downloadSelectedIndex].label}
              </Button>
              <Button
                size="small"
                disabled={isDownloading || rows.length === 0}
                onClick={(e) => setDownloadAnchorEl(e.currentTarget)}
              >
                <ExpandMoreIcon />
              </Button>
            </ButtonGroup>
            <Menu
              anchorEl={downloadAnchorEl}
              open={downloadOpen}
              onClose={() => setDownloadAnchorEl(null)}
            >
              {DOWNLOAD_OPTIONS.map((option, index) => (
                <MenuItem
                  key={option.label}
                  selected={index === downloadSelectedIndex}
                  onClick={() => {
                    setDownloadSelectedIndex(index);
                    setDownloadAnchorEl(null);
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="body2">{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.help_text}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <SummaryTile />
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 200,
          display: 'flex',
          flexDirection: 'column',
          alignItems: isLoading ? 'center' : 'stretch',
          justifyContent: isLoading ? 'center' : 'flex-start',
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              <Box sx={{ textAlign: 'left' }}>
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  // Exact same breakpoints as Teachers.js
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5,
                }}
              >
                {filteredRows.map((item_dict) => (
                  <SubstituteCard key={item_dict.id} substitute_dict={item_dict} />
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>

      {/* Edit Substitute Modal */}
      <Box>
        <EditModal
          open={isEditModalOpen}
          columns={substituteColumns}
          rowData={selectedEditRow}
          relatedData={relatedData}
          handleClose={handleEditModalClose}
          handleSave={handleEditSave}
          handleDelete={handleDelete}
          edit_title="Edit Substitute"
        />
      </Box>
    </Box>
  );
}
